// src/components/pages/WorkspacePlan.jsx
// Muestra el plan actual del workspace al admin, con limites y uso
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import { getWorkspaceUsage } from '../../services/planService';
import { PLANS, PLAN_ORDER, ALL_MODULES } from '../../config/plans';
import { createPaymentOrder, isMercadoPagoConfigured, getPlanPrice } from '../../services/mercadoPagoService';
import toast from 'react-hot-toast';
import {
  FiUsers, FiGrid, FiCheck, FiLock, FiLoader,
  FiTrendingUp, FiStar, FiZap, FiShield, FiMail,
  FiCreditCard, FiArrowRight
} from 'react-icons/fi';

const WorkspacePlan = () => {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const wsId = user?.workspaceId || user?.uid;
      if (!wsId) { setLoading(false); return; }
      try {
        const u = await getWorkspaceUsage(wsId);
        setUsage(u);
      } catch (err) {
        console.error('Error loading plan:', err);
      }
      setLoading(false);
    };
    load();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <FiLoader className="animate-spin text-indigo-500" size={24} />
      </div>
    );
  }

  if (!usage) {
    return (
      <div className="text-center py-12 text-gray-500 text-sm">
        No se pudo cargar la informacion del plan
      </div>
    );
  }

  const currentPlan = usage.planConfig;
  const usersPercentage = usage.users.percentage;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Current plan header */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Tu plan actual</h2>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${currentPlan.badge}`}>
                {currentPlan.name}
              </span>
            </div>
            <p className="text-sm text-gray-500">{currentPlan.description}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-gray-900 dark:text-white">{currentPlan.priceLabel}</div>
          </div>
        </div>
      </div>

      {/* Usage meters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Users */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <FiUsers size={16} className="text-indigo-500" />
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Usuarios</span>
          </div>
          <div className="flex items-end justify-between mb-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{usage.users.current}</span>
            <span className="text-sm text-gray-400">
              / {usage.users.max === Infinity ? 'Ilimitado' : usage.users.max}
            </span>
          </div>
          {usage.users.max !== Infinity && (
            <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all ${
                  usersPercentage > 90 ? 'bg-red-500' : usersPercentage > 70 ? 'bg-amber-500' : 'bg-indigo-500'
                }`}
                style={{ width: `${Math.min(usersPercentage, 100)}%` }}
              />
            </div>
          )}
          {usersPercentage > 80 && usage.users.max !== Infinity && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <FiTrendingUp size={12} /> Estas cerca del limite. Considera actualizar tu plan.
            </p>
          )}
        </div>

        {/* Modules */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <FiGrid size={16} className="text-purple-500" />
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Modulos</span>
          </div>
          <div className="flex items-end justify-between mb-3">
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{usage.modules.active}</span>
            <span className="text-sm text-gray-400">/ {usage.modules.total} disponibles</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ALL_MODULES.map(mod => {
              const included = usage.modules.available.includes(mod.key);
              return (
                <span
                  key={mod.key}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium ${
                    included
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-gray-50 text-gray-400 border border-gray-100 line-through'
                  }`}
                >
                  {included ? <FiCheck size={10} /> : <FiLock size={10} />}
                  {mod.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Plans comparison */}
      <div>
        <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-4">Planes disponibles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLAN_ORDER.map(planId => {
            const plan = PLANS[planId];
            const isCurrent = planId === usage.plan;
            const isUpgrade = PLAN_ORDER.indexOf(planId) > PLAN_ORDER.indexOf(usage.plan);

            return (
              <div
                key={planId}
                className={`rounded-2xl border p-5 transition-all ${
                  isCurrent
                    ? 'border-indigo-300 bg-indigo-50/50 dark:bg-indigo-900/20 ring-2 ring-indigo-200'
                    : 'border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {planId === 'free' && <FiStar size={16} className="text-gray-400" />}
                    {planId === 'pro' && <FiZap size={16} className="text-indigo-500" />}
                    {planId === 'enterprise' && <FiShield size={16} className="text-purple-500" />}
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-200">{plan.name}</span>
                  </div>
                  {isCurrent && (
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[10px] font-bold">
                      Actual
                    </span>
                  )}
                </div>

                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{plan.priceLabel}</div>
                <p className="text-xs text-gray-500 mb-4">{plan.description}</p>

                <ul className="space-y-2 mb-4">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <FiCheck size={12} className="text-emerald-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {isUpgrade && (
                  <UpgradeButton planId={planId} planName={plan.name} user={user} workspaceId={user?.workspaceId} />
                )}
                {isCurrent && (
                  <div className="w-full py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-500 text-center text-xs font-medium rounded-xl">
                    Plan actual
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Boton de upgrade con MercadoPago o email fallback
const UpgradeButton = ({ planId, planName, user, workspaceId }) => {
  const [processing, setProcessing] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  const mpConfigured = isMercadoPagoConfigured();

  const handleUpgrade = async (billing = 'monthly') => {
    if (!mpConfigured) {
      // Fallback a email si MercadoPago no esta configurado
      window.location.href = `mailto:vcecursos@gmail.com?subject=Upgrade a ${planName}&body=Hola, quiero actualizar mi plan a ${planName} (${billing === 'yearly' ? 'anual' : 'mensual'}) para mi empresa.`;
      return;
    }

    setProcessing(true);
    try {
      const order = await createPaymentOrder(workspaceId, planId, billing, {
        email: user?.email,
        name: user?.displayName,
      });
      toast.success('Orden creada. Redirigiendo al pago...');
      // Aqui se llamaria a la Cloud Function para crear la preferencia
      // y redirigir al checkout de MercadoPago
      // Por ahora solo mostramos que la orden fue creada
      toast('Pago con MercadoPago estara disponible pronto. Contacta a vcecursos@gmail.com', { icon: 'ℹ️', duration: 5000 });
    } catch (err) {
      toast.error('Error al crear orden');
      console.error(err);
    }
    setProcessing(false);
  };

  const price = getPlanPrice(planId, 'monthly');
  const yearlyPrice = getPlanPrice(planId, 'yearly');

  return (
    <div>
      {!showBilling ? (
        <button
          onClick={() => setShowBilling(true)}
          disabled={processing}
          className="w-full py-2.5 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          <FiCreditCard size={12} />
          Actualizar a {planName}
        </button>
      ) : (
        <div className="space-y-2">
          <button onClick={() => handleUpgrade('monthly')} disabled={processing}
            className="w-full py-2.5 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
            {processing ? <FiLoader className="animate-spin" size={12} /> : <FiArrowRight size={12} />}
            Mensual - S/{price?.amount}/mes
          </button>
          {yearlyPrice && (
            <button onClick={() => handleUpgrade('yearly')} disabled={processing}
              className="w-full py-2.5 bg-purple-600 text-white text-xs font-bold rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
              {processing ? <FiLoader className="animate-spin" size={12} /> : <FiStar size={12} />}
              Anual - S/{yearlyPrice?.amount}/ano (ahorra 20%)
            </button>
          )}
          <button onClick={() => setShowBilling(false)} className="w-full py-1.5 text-[10px] text-gray-400 hover:text-gray-600">
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkspacePlan;
